/**
 * AppointmentChecklistControls.js
 *
 * Compact breadcrumb-row controls that open the appointment checklist dialog,
 * plus the auto-open behaviour triggered by location.state from the overview
 * card's Start/Continue/Review action.
 *
 * This component is rendered via Breadcrumb's `rightSlot` prop on the seven
 * Adult Autism Developmental History and Observation proforma pages. It owns:
 * - The "Appointment Checklists:" label and "Start of Appt." / "End of Appt."
 *   trigger buttons
 * - The controlled AppointmentChecklistDialog
 * - One-shot auto-open logic reading location.state.openAppointmentChecklist
 *
 * The auto-open only fires on initial mount (guarded by a ref) and only when
 * the page is NOT in preview mode. After opening, the location entry is replaced
 * with the same state minus the consumed key — preserving sibling keys like
 * activeItemId that SSI needs for criterion restore.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useIsPreviewMode } from '../../hooks/useIsPreviewMode';
import AppointmentChecklistDialog from './AppointmentChecklistDialog';

const COMPACT_GHOST_BTN =
  'px-2.5 py-1 rounded-md border border-violet-900 text-violet-900 text-xs font-semibold hover:bg-violet-50 transition-colors whitespace-nowrap';

export default function AppointmentChecklistControls({ appointmentType = 'developmentalHistory' }) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('start');
  const isPreview = useIsPreviewMode();
  const location = useLocation();
  const navigate = useNavigate();
  const didAutoOpen = useRef(false);

  // One-shot auto-open: if location.state carries the signal and we have not
  // already processed it on this mount, open the Start tab and strip the key.
  useEffect(() => {
    if (didAutoOpen.current) return;
    if (isPreview) return;
    if (location.state?.openAppointmentChecklist !== 'start') return;

    didAutoOpen.current = true;
    setActiveTab('start');
    setDialogOpen(true);

    // Replace current history entry, removing only the consumed key while
    // preserving any sibling state (e.g. activeItemId for SSI restore).
    const { openAppointmentChecklist, ...rest } = location.state; // eslint-disable-line no-unused-vars
    navigate(location.pathname + location.search, {
      replace: true,
      state: Object.keys(rest).length > 0 ? rest : undefined,
    });
  }, [location.state, location.pathname, location.search, navigate, isPreview]);

  const handleOpenStart = useCallback(() => {
    setActiveTab('start');
    setDialogOpen(true);
  }, []);

  const handleOpenEnd = useCallback(() => {
    setActiveTab('end');
    setDialogOpen(true);
  }, []);

  return (
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-gray-600 whitespace-nowrap">
        Appointment Checklists:
      </span>
      <button
        type="button"
        onClick={handleOpenStart}
        className={COMPACT_GHOST_BTN}
      >
        Start of Appt.
      </button>
      <button
        type="button"
        onClick={handleOpenEnd}
        className={COMPACT_GHOST_BTN}
      >
        End of Appt.
      </button>

      <AppointmentChecklistDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        appointmentType={appointmentType}
      />
    </div>
  );
}
